import React, { useState } from 'react';

import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNotification } from '../../hooks/useNotification';
import { rateUserMemory } from '../../services/api';
import type { UserMemoryFeedbackResult, UserMemoryRating } from '../../types';

interface MemoryFeedbackButtonsProps {
  publicId: string;
  disabled?: boolean;
  onRated: (result: UserMemoryFeedbackResult) => void;
}

export const MemoryFeedbackButtons: React.FC<MemoryFeedbackButtonsProps> = ({
  publicId,
  disabled,
  onRated,
}) => {
  const { t } = useTranslation();
  const { success, error: notifyError } = useNotification();
  const [pending, setPending] = useState<UserMemoryRating | null>(null);

  const rate = async (rating: UserMemoryRating) => {
    setPending(rating);
    const response = await rateUserMemory(publicId, rating);

    if (response.success && response.data) {
      success(
        t(
          response.data.status === 'ACTIVE'
            ? 'settings.memory.messages.rated'
            : 'settings.memory.messages.retired'
        )
      );
      onRated(response.data);
    } else {
      notifyError(response.error || t('settings.memory.errors.rateFailed'));
    }

    setPending(null);
  };

  const renderButton = (rating: UserMemoryRating, icon: React.ReactNode) => (
    <Tooltip title={t(`settings.memory.actions.${rating}`)}>
      <span>
        <IconButton
          type="button"
          size="small"
          disabled={disabled || pending !== null}
          onClick={() => void rate(rating)}
        >
          {pending === rating ? <CircularProgress size={16} /> : icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      {renderButton('like', <ThumbsUp size={16} />)}
      {renderButton('dislike', <ThumbsDown size={16} />)}
    </>
  );
};
